import React from "react";
import moment from "moment";
import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state";
import Popover from "@mui/material/Popover";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import SoftBox from "../SoftBox";
import SoftTypography from "../SoftTypography";
import { ReactComponent as IntegratedIcon } from "../../img/timeline-integratedicon.svg";
import { formatNumber } from "../../helpers/formatter";
import { formatKpiPercent } from "../../helpers/scoreAggregate";
import { getAdvancedGameSettingsFromKpi, resolveGamePointsRule } from "../../constants/kpi";

// The app's one signature accent — the same magenta→violet sweep already on
// the bolt icon and the selected-tile border (Timeline.jsx). Reused here
// rather than inventing a new palette for this popover.
const BRAND_GRADIENT = "linear-gradient(135deg, #FF0080 0%, #7928CA 100%)";

const RESET_FREQUENCY_LABEL = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

const METRIC_TYPE_LABEL = {
  progressive: "Progressive — adds up over the period",
  anchor: "Anchor — scored against a benchmark",
  display: "Display only — tracked, not scored",
};

// Admins write the integration description as "<Metric Name>-<blurb>" (see
// the Housecall Pro mapping copy), which reads redundant right under a
// header that already says the metric's name. Strip that leading echo so
// the popover starts at the actual sentence.
function stripTitleEcho(description, title) {
  if (!description) return description;
  const trimmedTitle = (title || "").trim();
  if (!trimmedTitle) return description.trim();
  const escaped = trimmedTitle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const echoPattern = new RegExp(`^\\s*${escaped}\\s*[-–—:|]+\\s*`, "i");
  return description.replace(echoPattern, "").trim();
}

function InfoRow({ icon, label, value, emphasize }) {
  return (
    <SoftBox sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
      <SoftBox
        sx={{
          flex: "none",
          width: 23,
          height: 23,
          borderRadius: "7px",
          display: "grid",
          placeItems: "center",
          background: "#F6F4FE",
          color: "#7928CA",
          "& svg": { fontSize: "12.5px" },
        }}
      >
        {icon}
      </SoftBox>
      <SoftBox sx={{ minWidth: 0, paddingTop: "1px" }}>
        <SoftTypography
          sx={{
            display: "block",
            fontFamily: "DM Sans",
            fontSize: "9px",
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: "#98A2B3",
          }}
        >
          {label}
        </SoftTypography>
        <SoftTypography
          sx={{
            display: "block",
            fontFamily: "DM Sans",
            fontSize: "12px",
            fontWeight: emphasize ? 700 : 500,
            color: emphasize ? "#101828" : "#344054",
            lineHeight: 1.42,
            marginTop: "2px",
          }}
        >
          {value}
        </SoftTypography>
      </SoftBox>
    </SoftBox>
  );
}

/**
 * Info popover behind the lightning-bolt icon Scorekeeper shows next to an
 * integrated metric's column header. Admins asked for a quick way to see how
 * an integrated column is wired up without opening the full metric editor:
 * where its data comes from, how points/goal are configured, etc.
 */
export default function IntegratedMetricInfo({ kpi }) {
  if (!kpi) return null;

  const kind = kpi.kind ?? kpi.model?.kind;
  const fmt = (val) => {
    if (kind === "%") return formatKpiPercent(kpi, Number(val) || 0);
    return formatNumber(Math.round(Number(val) || 0), kind);
  };

  const advanced = getAdvancedGameSettingsFromKpi(kpi);
  const { metricAmount, pointsEarned } = resolveGamePointsRule(kpi, advanced);
  const isAnchor = kpi.metricType === "anchor";
  const lowerIsBetter = advanced.effectType === "negative";
  const opt = kpi.optional || {};

  // ---- how points are earned ------------------------------------------
  let earnText;
  if (advanced.monitorMode) {
    earnText = "Tracked only — this metric earns no points.";
  } else if (isAnchor) {
    const above = Number(opt.aboveBenchmark ?? 0);
    const below = Number(opt.belowBenchmark ?? 0);
    const step = fmt(opt.unit ?? 1);
    earnText = `+${above} pt${above === 1 ? "" : "s"} per ${step} above the benchmark${
      below ? `, −${below} pt${below === 1 ? "" : "s"} per ${step} below it` : ""
    }.`;
  } else {
    let amt = Number(metricAmount) || 1;
    let pts = Number(pointsEarned) || 0;
    if (pts > 0 && pts < 1) {
      amt /= pts;
      pts = 1;
    }
    pts = Math.round(pts * 100) / 100;
    earnText = `Every ${fmt(amt)} = ${pts} point${pts === 1 ? "" : "s"}.`;
  }

  // ---- goal --------------------------------------------------------
  const goalOverride = (kpi.gameConfig?.dailyGoalText ?? kpi.dailyGoalText ?? "").trim();
  let goalText;
  if (goalOverride) {
    goalText = goalOverride;
  } else if (isAnchor) {
    goalText = `${lowerIsBetter ? "Stay under" : "Stay above"} ${fmt(kpi.target)}`;
  } else {
    const freq = RESET_FREQUENCY_LABEL[advanced.resetFrequency]?.toLowerCase() || "day";
    goalText = `${fmt(kpi.target)} per ${freq}${lowerIsBetter ? " (lower is better)" : ""}`;
  }

  const sourceLabel = kpi.sourceName || kpi.source || "Integration";
  const cleanDescription = stripTitleEcho(kpi.sourceDescription, kpi.title || kpi.name);
  const EffectIcon = lowerIsBetter ? TrendingDownRoundedIcon : TrendingUpRoundedIcon;

  // Real connection state from the company's Integration doc (see
  // getCompanyKpis on the server). `lastSyncedReliable` is false for the
  // couple of integrations whose pull code never advances their sync
  // timestamp — for those we still show connected/disconnected, just not a
  // (misleading, possibly very stale) time.
  const isDisconnected = kpi.connectionStatus === "disconnected";
  const syncDotColor = isDisconnected ? "#F04438" : "#12B76A";
  let syncLabel = null;
  if (kpi.connectionStatus === "connected") {
    syncLabel = kpi.lastSyncedAt && kpi.lastSyncedReliable !== false
      ? `Synced ${moment(kpi.lastSyncedAt).fromNow()}`
      : "Live sync";
  } else if (isDisconnected) {
    syncLabel = "Disconnected";
  }

  return (
    <PopupState variant="popover" popupId={`integrated-metric-info-${kpi._id}`}>
      {(popupState) => (
        <>
          <SoftBox
            {...bindTrigger(popupState)}
            onClick={(e) => {
              e.stopPropagation();
              bindTrigger(popupState).onClick(e);
            }}
            aria-label={`How the ${kpi.title || kpi.name} integration works`}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              cursor: "pointer",
              borderRadius: "8px",
              transition: "transform 0.16s cubic-bezier(0.16, 1, 0.3, 1), filter 0.16s cubic-bezier(0.16, 1, 0.3, 1)",
              "& svg": { display: "block" },
              "&:hover": {
                transform: "scale(1.12)",
                filter: "drop-shadow(0 2px 6px rgba(255, 0, 128, 0.35))",
              },
              "&:active": { transform: "scale(0.96)" },
            }}
          >
            <IntegratedIcon />
          </SoftBox>
          <Popover
            {...bindPopover(popupState)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            onClick={(e) => e.stopPropagation()}
            marginThreshold={12}
            TransitionProps={{
              timeout: 220,
              easing: { enter: "cubic-bezier(0.16, 1, 0.3, 1)", exit: "cubic-bezier(0.4, 0, 1, 1)" },
              style: { transformOrigin: "left top" },
            }}
            sx={{
              "& .MuiPaper-root": {
                // The brand wash lives on the PAPER itself, not a box inside
                // the header — painted as one continuous gradient across the
                // whole card so it fades out gradually well past the header
                // into the score-rows section below, instead of stopping
                // dead at the header's own box edge (which read as a hard
                // seam once the header and body were two separate fills).
                background:
                  "radial-gradient(400px 350px at -50px -60px, rgba(255,0,128,0.20) 0%, rgba(219,20,152,0.11) 30%, rgba(121,40,202,0.05) 52%, rgba(121,40,202,0.015) 70%, rgba(255,255,255,0) 88%), " +
                  "linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.94) 100%)",
                backdropFilter: "blur(20px) saturate(160%)",
                WebkitBackdropFilter: "blur(20px) saturate(160%)",
                boxShadow:
                  "0px 24px 56px -18px rgba(121, 40, 202, 0.32), 0px 4px 14px -4px rgba(16,24,40,0.12), inset 0px 1px 0px 0px rgba(255,255,255,0.8)",
                border: "1px solid rgba(255,255,255,0.7)",
                borderRadius: "14px",
                width: 264,
                maxWidth: "calc(100vw - 24px)",
                // The card grows to fit its content (a longer integration
                // description, more rows) instead of clipping it. Only once
                // the whole thing genuinely can't fit the viewport does this
                // single scrollbar appear — no separate inner scroll region
                // fighting it for the remaining height.
                maxHeight: "calc(100vh - 32px)",
                overflowY: "auto",
                overflowX: "hidden",
                marginTop: "10px",
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-thumb": { background: "#E4D9F7", borderRadius: "999px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
              },
            }}
          >
            <SoftBox
              sx={{
                position: "relative",
                padding: "16px 16px 14px",
              }}
            >
              <SoftBox sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <SoftBox
                  sx={{
                    flex: "none",
                    width: 28,
                    height: 28,
                    borderRadius: "9px",
                    display: "grid",
                    placeItems: "center",
                    background: BRAND_GRADIENT,
                    boxShadow: "0 3px 10px -3px rgba(255, 0, 128, 0.45)",
                    "& svg": { width: 18, height: 18, filter: "brightness(0) invert(1)" },
                  }}
                >
                  <IntegratedIcon />
                </SoftBox>
                <SoftTypography
                  sx={{
                    fontFamily: "DM Sans",
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#101828",
                    lineHeight: 1.3,
                  }}
                >
                  {kpi.title || kpi.name}
                </SoftTypography>
              </SoftBox>

              <SoftBox
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  marginTop: "10px",
                  padding: "4px 9px 4px 8px",
                  borderRadius: "999px",
                  background: "#FFFFFF",
                  border: "1px solid #EFE6FB",
                }}
              >
                <SoftBox
                  sx={{
                    flex: "none",
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: syncDotColor,
                    boxShadow: `0 0 0 0 ${isDisconnected ? "rgba(240, 68, 56, 0.55)" : "rgba(18, 183, 106, 0.55)"}`,
                    "@keyframes gmbLiveDotPulse": {
                      "0%": { boxShadow: `0 0 0 0 ${isDisconnected ? "rgba(240, 68, 56, 0.55)" : "rgba(18, 183, 106, 0.55)"}` },
                      "70%": { boxShadow: `0 0 0 5px ${isDisconnected ? "rgba(240, 68, 56, 0)" : "rgba(18, 183, 106, 0)"}` },
                      "100%": { boxShadow: `0 0 0 0 ${isDisconnected ? "rgba(240, 68, 56, 0)" : "rgba(18, 183, 106, 0)"}` },
                    },
                    animation: isDisconnected ? "none" : "gmbLiveDotPulse 2.2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                  }}
                />
                <SoftTypography sx={{ fontFamily: "DM Sans", fontSize: "11px", fontWeight: 700, color: "#101828" }}>
                  {sourceLabel}
                </SoftTypography>
                {syncLabel && (
                  <SoftTypography
                    sx={{
                      fontFamily: "DM Sans",
                      fontSize: "9.5px",
                      fontWeight: 600,
                      color: isDisconnected ? "#F04438" : "#12B76A",
                    }}
                  >
                    · {syncLabel}
                  </SoftTypography>
                )}
              </SoftBox>

              {cleanDescription && (
                <SoftTypography
                  sx={{
                    position: "relative",
                    display: "block",
                    fontFamily: "DM Sans",
                    fontSize: "11.5px",
                    fontWeight: 600,
                    color: "#3B2E63",
                    lineHeight: 1.5,
                    marginTop: "10px",
                    padding: "9px 11px",
                    borderRadius: "10px",
                    background: "#F9F5FF",
                  }}
                >
                  {cleanDescription}
                </SoftTypography>
              )}
            </SoftBox>

            <SoftBox
              sx={{
                padding: "14px 16px 16px",
                borderTop: "1px solid #F0F2F6",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <SoftBox sx={{ display: "flex", gap: "10px" }}>
                <SoftBox sx={{ flex: 1, minWidth: 0 }}>
                  <InfoRow
                    icon={<AutorenewRoundedIcon />}
                    label="Resets"
                    value={RESET_FREQUENCY_LABEL[advanced.resetFrequency] || "Daily"}
                  />
                </SoftBox>
                <SoftBox sx={{ flex: 1, minWidth: 0 }}>
                  <InfoRow
                    icon={<EffectIcon />}
                    label="Effect"
                    value={lowerIsBetter ? "Lower is better" : "Higher is better"}
                  />
                </SoftBox>
              </SoftBox>

              <InfoRow
                icon={<LayersRoundedIcon />}
                label="Metric type"
                value={METRIC_TYPE_LABEL[kpi.metricType] || kpi.metricType || "—"}
              />
              <InfoRow icon={<EmojiEventsRoundedIcon />} label="How to earn points" value={earnText} emphasize />
              <InfoRow icon={<FlagRoundedIcon />} label="Goal" value={goalText} emphasize />
            </SoftBox>
          </Popover>
        </>
      )}
    </PopupState>
  );
}
